import { useState } from "react";

import type {
  ScopeMatrixDisplayRow,
  ScopeMatrixDisplayViewModel
} from "../viewmodel/scopeMatrixAdapter";
import { projectLabelFromId } from "../app/format";
import {
  FacetFilterBar,
  labelForFacetValue,
  scopeGroupByOptions as sharedScopeGroupByOptions,
  toneForFacetValue,
  useFacetFilter,
  type UseFacetFilterConfig
} from "../components/facets";
import { AiProvenanceBadge, cx, DataGapPanel } from "./shared";

const scopeStatusTone = {
  included: {
    label: "Included",
    className: "bg-[#EAF1E6] text-[#4E7644]"
  },
  by_others: {
    label: "By others",
    className: "bg-[#ECEFF3] text-[#5E6E82]"
  },
  excluded: {
    label: "Excluded",
    className: "bg-[var(--line2)] text-[var(--muted2)]"
  }
} as const;

const scopeFacetConfigKey = (facetKey: string): string =>
  facetKey === "responsibility" ? "scopeStatus" : facetKey;

const labelForScopeFacet = (facetKey: string, value: string): string =>
  labelForFacetValue(scopeFacetConfigKey(facetKey), value);

const toneForScopeFacet = (facetKey: string, value: string) =>
  toneForFacetValue(scopeFacetConfigKey(facetKey), value);

const scopeFacetDefs: UseFacetFilterConfig<ScopeMatrixDisplayRow>["facetDefs"] = [
  {
    key: "responsibility",
    label: "Status",
    multiSelect: false,
    valueOf: (row) => row.facets.responsibility,
    toneOf: (value) => toneForScopeFacet("responsibility", value)
  },
  {
    key: "system",
    label: "System",
    valueOf: (row) => row.facets.system,
    toneOf: (value) => toneForScopeFacet("system", value)
  },
  {
    key: "workType",
    label: "Work-type",
    valueOf: (row) => row.facets.workType,
    toneOf: (value) => toneForScopeFacet("workType", value)
  },
  {
    key: "commercial",
    label: "Commercial",
    valueOf: (row) => row.facets.commercial,
    toneOf: (value) => toneForScopeFacet("commercial", value)
  }
];

const sharedScopeGroupByOptionByValue = new Map<string | null, string>(
  sharedScopeGroupByOptions.map((option) => [option.value, option.label])
);

const scopeScreenGroupByOptions: { value: string | null; label: string }[] = [
  { value: "responsibility", label: "Status" },
  { value: "system", label: sharedScopeGroupByOptionByValue.get("system") ?? "System" },
  { value: "workType", label: "Work-type" }
];

const rowCountLabel = (count: number): string =>
  count === 1 ? "1 row" : `${count} rows`;

function ScopeStatusChip({ row }: { row: ScopeMatrixDisplayRow }) {
  const tone = scopeStatusTone[row.status] ?? scopeStatusTone.included;

  return (
    <span
      className={cx(
        "inline-flex whitespace-nowrap rounded-full px-4 py-2 text-[11.5px] font-extrabold",
        tone.className
      )}
    >
      {tone.label}
    </span>
  );
}

export function ScopeScreen({ view }: { view: ScopeMatrixDisplayViewModel }) {
  const [selectedRows, setSelectedRows] = useState<Record<string, boolean>>({});
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const projectLabel = projectLabelFromId(view.projectId);
  const selectedCount = view.rows.filter((row) => selectedRows[row.key]).length;
  const sourceRefCount = view.rows.reduce((sum, row) => sum + row.sourceRefCount, 0);
  const ruleTraceRefCount = view.rows.reduce(
    (sum, row) => sum + row.ruleTraceRefCount,
    0
  );
  const scopeFilters = useFacetFilter({
    items: view.rows,
    facetDefs: scopeFacetDefs,
    labelFor: labelForScopeFacet,
    initialGroupBy: "responsibility"
  });

  const toggleRow = (row: ScopeMatrixDisplayRow) => {
    setSelectedRows((current) => {
      const next = { ...current };

      if (next[row.key]) {
        delete next[row.key];
      } else {
        next[row.key] = true;
      }

      return next;
    });
  };

  const toggleGroup = (groupKey: string) => {
    setCollapsedGroups((current) => ({
      ...current,
      [groupKey]: !current[groupKey]
    }));
  };

  return (
    <div className="flex-1 overflow-auto px-8 py-[34px] pb-[76px] xl:px-14">
      <div className="mx-auto max-w-[900px]">
        <div className="flex flex-wrap items-end justify-between gap-5">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[.08em] text-[var(--faint)]">
              {projectLabel} · whole job
            </div>
            <h1 className="m-0 mt-1 text-[26px] font-extrabold tracking-[-.03em]">
              Scope — what we're providing
            </h1>
            <p className="m-0 mt-2 max-w-[610px] text-[13.5px] leading-[1.5] text-[var(--muted)]">
              Controls supply boundaries rolled up from generated project scope, with
              installation work kept out of priced quantities.
            </p>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <span className="rounded-full bg-[var(--chip)] px-3 py-1.5 text-[11px] font-bold text-[var(--muted)]">
              {view.curatedRowCount} grouped rows
            </span>
            <span className="rounded-full bg-[var(--chip)] px-3 py-1.5 text-[11px] font-bold text-[var(--muted)]">
              {view.realRowCount} scope items
            </span>
            {selectedCount > 0 ? (
              <span className="rounded-full bg-[var(--ink)] px-3 py-1.5 text-[11px] font-bold text-[var(--onSolid)]">
                {selectedCount} selected
              </span>
            ) : null}
          </div>
        </div>

        <div className="mt-[18px]" data-showme="scope-filters">
          <FacetFilterBar
            facets={scopeFilters.facets}
            selection={scopeFilters.selection}
            onToggle={scopeFilters.toggle}
            onClearAll={scopeFilters.clearAll}
            groupBy={scopeFilters.groupBy}
            groupByOptions={scopeScreenGroupByOptions}
            onGroupByChange={scopeFilters.setGroupBy}
          />
        </div>

        <section className="mt-[16px]">
          {view.rows.length > 0 ? (
            <div className="mb-3 rounded-[12px] border border-[#D7E1EA] bg-[#F6FAFD] px-4 py-3">
              <AiProvenanceBadge
                why="These scope items were grouped from the saved specification."
                where={`Where: ${sourceRefCount} source links and ${ruleTraceRefCount} design checks remain attached to this section.`}
              />
            </div>
          ) : null}
          {view.rows.length === 0 ? (
            <div className="rounded-[14px] border border-[var(--line)] bg-[var(--surface)] px-5 py-8 text-center text-[13px] font-semibold text-[var(--muted)]">
              No generated scope rows are available for this project.
            </div>
          ) : scopeFilters.filtered.length === 0 ? (
            <div className="rounded-[14px] border border-[var(--line)] bg-[var(--surface)] px-5 py-8 text-center text-[13px] font-semibold text-[var(--muted)]">
              No scope rows match these filters.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {scopeFilters.groups.map((group) => {
                const groupKey = `${scopeFilters.groupBy ?? "all"}:${group.key}`;
                const collapsed = !!collapsedGroups[groupKey];

                return (
                  <section
                    key={groupKey}
                    className="overflow-hidden rounded-[14px] border border-[var(--line)] bg-[var(--surface)] shadow-[0_1px_2px_rgba(35,33,30,.04)]"
                  >
                    <button
                      type="button"
                      aria-expanded={!collapsed}
                      onClick={() => toggleGroup(groupKey)}
                      className={cx(
                        "flex w-full cursor-pointer items-center justify-between gap-4 px-5 py-3.5 text-left hover:bg-[var(--chip)] focus:outline-none focus:ring-2 focus:ring-[var(--ink)] focus:ring-inset",
                        !collapsed && "border-b border-[var(--line2)]"
                      )}
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-[13.5px] font-extrabold tracking-[-.01em] text-[var(--ink)]">
                          {group.label}
                        </span>
                        <span className="mt-0.5 block text-[11px] font-bold text-[var(--muted2)]">
                          {rowCountLabel(group.items.length)}
                        </span>
                      </span>
                      <span
                        aria-hidden="true"
                        className="flex h-7 w-7 flex-none items-center justify-center rounded-full border border-[var(--line3)] bg-[var(--surface)] text-[15px] font-extrabold leading-none text-[var(--muted)]"
                      >
                        {collapsed ? "+" : "-"}
                      </span>
                    </button>

                    {!collapsed ? (
                      <div>
                        {group.items.map((row) => (
                          <div
                            key={row.key}
                            className="grid grid-cols-[22px_minmax(0,1fr)_auto] items-center gap-[18px] border-t border-[var(--line2)] px-5 py-4 first:border-t-0"
                          >
                            <input
                              type="checkbox"
                              checked={!!selectedRows[row.key]}
                              onChange={() => toggleRow(row)}
                              aria-label={`Select ${row.label}`}
                              className="h-[18px] w-[18px] cursor-pointer accent-[var(--ink)]"
                            />
                            <div className="min-w-0">
                              <div className="text-[14.5px] font-extrabold tracking-[-.01em] text-[var(--ink)]">
                                {row.label}
                              </div>
                              <p className="m-0 mt-1 text-[12.5px] leading-[1.45] text-[var(--muted)]">
                                {row.rationale}
                              </p>
                            </div>
                            <ScopeStatusChip row={row} />
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </section>
                );
              })}
            </div>
          )}

          <div className="mt-[14px] rounded-[12px] border border-[var(--line)] bg-[var(--surface)] px-4 py-3 text-[12px] font-semibold leading-[1.5] text-[var(--faint)]">
            {view.installNoteFooter}
          </div>
        </section>

        <div className="mt-4">
          <DataGapPanel gaps={view.dataGaps} />
        </div>
      </div>
    </div>
  );
}
